import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, BackHandler, Image, StyleSheet, View } from 'react-native';
import { useIAP } from 'react-native-iap';
import { useFocusEffect } from '@react-navigation/native';
import { TWCTile } from '@components';
import axios from 'axios';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';

import { assets } from '@assets';
import { apiConfig, onboardingTestIDs, Theme } from '@constants';
import { useContextNodeHandle } from '@hooks';
import { navigationService } from '@services';
import { onboardingFlowState, onboardingLandingState, onboardingPlansState, userSettingsState } from '@store';

import { OnboardingBackgroundImage } from './OnboardingBackgroundImage';

import { TWCText } from '../../../components/primitives/TWCText/TWCText';
import { SegmentIdentifyUser } from '../../../components/segment/SegmentIdentifyUser';
import { getDspAuthHeader } from '../../../helpers/DspAuth';
import { DEBUG, isAndroid } from '../../../helpers/Platform';
import { verifyAppInstalled } from '../../../helpers/verifyAppInstalled';
import { getUserProducts } from '../../../hooks/api/useGetUserProducts/useGetUserProducts';

const SubscriptionSelectionContentAPI = () => {
  const { connected, subscriptions, currentPurchase, getSubscriptions, requestSubscription, finishTransaction } =
    useIAP();

  const resetOnboardingFlow = useResetRecoilState(onboardingFlowState);
  const fetchedIAP = useRef(false);

  const { data: landingData } = useRecoilValue(onboardingLandingState);
  const { data: planData } = useRecoilValue(onboardingPlansState);

  if (planData && (!subscriptions || !connected || !fetchedIAP.current)) {
    const monthlySKU = planData.MonthlyButton.link;
    const yearlySKU = planData.YearlyButton.link;

    if (monthlySKU && yearlySKU) {
      getSubscriptions([monthlySKU, yearlySKU]);
      fetchedIAP.current = true;
    }
  }

  useFocusEffect(
    useCallback(() => {
      const onBackPress = () => {
        resetOnboardingFlow();
        // Navigate back to sign up page, skipping the code verification page.
        navigationService.navigate(onboardingTestIDs.pages.subscriberSignUpPage);

        return true;
      };

      BackHandler.addEventListener('hardwareBackPress', onBackPress);

      return () => BackHandler.removeEventListener('hardwareBackPress', onBackPress);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
  );

  return (
    <SubscriptionSelectionPageContentUI
      baseData={landingData}
      currentPurchase={currentPurchase}
      finishTransaction={finishTransaction}
      planData={planData}
      requestSubscription={requestSubscription}
      subscriptions={subscriptions ?? []}
    />
  );
};

const fallbackSubscriptionsArr = [
  {
    productId: 'TWC2999Yearly',
    price: '29.99',
  },
  {
    productId: 'TWC299Monthly',
    price: '2.99',
  },
];

const placeholderText = {
  heading: 'Choose Your Plan',
  subheading: 'Billing begins when your 7-day free trial ends. Cancel or change plans anytime.',
  trialTermsText: '7-Day free trial available only to new users.',
  planDisclaimerText: 'These plans do not include access to weather.com website or mobile app Premium Subscriptions.',
};

const TILE_SIZES = {
  height: 240,
  width: 450,
};

const tPath = 'subscriptionSelectionPage';

const SubscriptionSelectionPageContentUI = ({
  baseData,
  planData,
  subscriptions,
  currentPurchase,
  requestSubscription,
  finishTransaction,
}) => {
  const monthlyTileHandle = useContextNodeHandle(onboardingTestIDs.subscriptionSelection.monthlyTile);
  const yearlyTileHandle = useContextNodeHandle(onboardingTestIDs.subscriptionSelection.yearlyTile);

  const [linkedPurchase, setLinkedPurchase] = useState(null);
  const [loadingTransaction, setLoadingTransaction] = useState(false);

  const [userSettings, setUserSettings] = useRecoilState(userSettingsState);

  const linkAmazonPurchaseToDSP = async (purchase, retry = false) => {
    const { purchaseToken, transactionReceipt, userIdAmazon, userMarketplaceAmazon } = purchase;

    setLoadingTransaction(true);

    const { sku, purchaseDate } = JSON.parse(transactionReceipt);
    const dspBody = {
      orderId: purchaseToken,
      packageName: 'com.weathergroup.twc',
      productId: sku,
      purchaseTime: purchaseDate,
      purchaseState: 'SUCCESSFUL',
      purchaseToken,
      autoRenewing: {
        userId: userIdAmazon,
        marketplace: userMarketplaceAmazon,
      },
      signature: JSON.parse(transactionReceipt),
    };

    const headers = await getDspAuthHeader(retry);

    try {
      const { status } = await axios.post(apiConfig.AMAZON_LINK_PURCHASE_URL, dspBody, { headers });

      if (status === 409 || (status >= 200 && status < 300)) {
        setLinkedPurchase(purchase);
      } else {
        setLoadingTransaction(false);
        navigationService.navigate(onboardingTestIDs.pages.errorPage);
      }
    } catch (error) {
      console.error(error);
      setLoadingTransaction(false);
      if (retry) {
        navigationService.navigate(onboardingTestIDs.pages.errorPage);
      } else {
        linkAmazonPurchaseToDSP(purchase, true);
      }
    }
  };

  const handleSubscriptionPurchaseError = (error, sku) => {
    console.log(error.code);
    switch (error.code ?? '') {
      case 'E_ALREADY_OWNED':
        // Wait for current purchase to be fetched and attempt to link
        setLoadingTransaction(true);
        break;
      case 'E_ITEM_UNAVAILABLE':
      case 'E_NETWORK_ERROR':
      case 'E_SERVICE_ERROR':
        // Go to error page
        setLoadingTransaction(false);
        navigationService.navigate(onboardingTestIDs.pages.errorPage);
    }
  };

  const noFocusMonthly = isAndroid ? monthlyTileHandle : null;
  const noFocusYearly = isAndroid ? yearlyTileHandle : null;

  const monthly = { ...(planData?.MonthlyButton ?? {}) };
  const yearly = { ...(planData?.YearlyButton ?? {}) };

  subscriptions = subscriptions.length > 0 ? subscriptions : fallbackSubscriptionsArr;

  for (const subIndex in subscriptions) {
    const subscription = subscriptions[subIndex];

    if (monthly.link && subscription.productId === monthly.link) {
      monthly.price = subscription.price;
      monthly.sku = subscription.productId;
    } else if (yearly.link && subscription.productId === yearly.link) {
      yearly.price = subscription.price;
      yearly.sku = subscription.productId;
    } else {
      console.log(`unmatched subscription: ${subscription.productId}`);
    }
  }

  const { t } = useTranslation('translation', {
    keyPrefix: tPath,
  });

  useEffect(() => {
    if (currentPurchase) {
      if (DEBUG) {
        console.log('finishing in app purchase:', currentPurchase);
      }
      finishTransaction(currentPurchase, false).then(
        () => {
          linkAmazonPurchaseToDSP(currentPurchase);
        },
        error => {
          setLoadingTransaction(false);
          console.error(error);
          navigationService.navigate(onboardingTestIDs.pages.errorPage);
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPurchase, finishTransaction]);

  const getProducts = async (retry = false) => {
    try {
      const headers = await getDspAuthHeader(retry);
      const { products, platform } = await getUserProducts(headers);

      if (products.svod.length > 0) {
        const {
          customer,
          product,
          next_assessment_at,
          trial_started_at,
          state,
          previous_state,
          activated_at,
          scheduled_cancellation_at,
        } = products.svod[0];
        const newSettings = {
          ...userSettings,
          firstName: customer.first_name,
          lastName: customer.last_name,
          email: customer.email,
          pricePlan: product.name,
          billDate: next_assessment_at,
          trialStart: trial_started_at,
          subscriptionStatus: state,
          priorSubscriptionStatus: previous_state,
          dspCustomerId: customer.id,
          isPaidSubscriber: true,
          substart: activated_at,
          scheduledcancellationat: scheduled_cancellation_at,
          subscriptionPlatform: platform,
          intervalUnit: product.interval_unit,
        };

        setUserSettings(newSettings);
        const isValid = state === 'active' || state === 'trialing';

        setLoadingTransaction(false);
        if (isValid) {
          SegmentIdentifyUser(newSettings, false);
          verifyAppInstalled(newSettings);
          navigationService.navigate(onboardingTestIDs.pages.successPage);
        } else {
          navigationService.navigate(onboardingTestIDs.errorPage);
        }
      }
    } catch (productsError) {
      setLoadingTransaction(false);
      if (productsError.response) {
        console.log(productsError);
        // if this our first time through, and the status is 403, meaning expired token,
        // so retry (which will force a new token)
        const { status } = productsError.response;

        if (!retry && status === 403) {
          getProducts(true);
        } else {
          navigationService.navigate(onboardingTestIDs.errorPage);
        }
      }
    }
  };

  useEffect(() => {
    if (linkedPurchase) {
      getProducts(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedPurchase]);

  return (
    <OnboardingBackgroundImage
      testID={onboardingTestIDs.onboardingHome.pageBackgroundImage}
      uri={baseData?.backgroundImage}
    >
      {loadingTransaction && (
        <View style={styles.loadingIndicatorView}>
          <ActivityIndicator noTestID color={Theme.mainText} size={'large'} />
        </View>
      )}
      <View style={styles.pageContent}>
        <Image source={assets.twcLogo} style={styles.logo} />

        <TWCText
          style={styles.headingText}
          testID={onboardingTestIDs.subscriptionSelection.headingText}
          variant={'boldText54'}
        >
          {planData?.Headline?.headline ?? placeholderText.heading}
        </TWCText>
        <TWCText
          style={styles.subHeadingText}
          testID={onboardingTestIDs.subscriptionSelection.subheadingText}
          variant={'regularText36'}
        >
          {planData?.Headline?.body ?? placeholderText.subheading}
        </TWCText>
        <View style={styles.tileRow}>
          <TWCTile
            accessibilityHint={t('accessibilityHintTile')}
            accessibilityLabel={t('accessibilityLabelTileMonthly', {
              price: monthly.price,
              savingsPercentage: monthly.savingsPercentage,
            })}
            backgroundDefault={styles.tileDefault}
            height={TILE_SIZES.height}
            nextFocusDown={noFocusMonthly}
            nextFocusLeft={noFocusMonthly}
            nextFocusRight={yearlyTileHandle}
            nextFocusUp={noFocusMonthly}
            styleFocused={styles.tileFocused}
            testID={onboardingTestIDs.subscriptionSelection.monthlyTile}
            useFocusBorder={false}
            useLinearGradient={false}
            width={TILE_SIZES.width}
            onPress={() => {
              if (monthly.sku) {
                requestSubscription(monthly.sku).then(
                  () => {},
                  error => {
                    handleSubscriptionPurchaseError(error, monthly.sku);
                  },
                );
              }
            }}
          >
            {monthly.sku ? (
              <View style={Theme.styles.center}>
                <TWCText noTestID variant={'boldText48'}>
                  {`$${monthly.price} / mo`}
                </TWCText>
              </View>
            ) : (
              <View style={Theme.styles.center}>
                <ActivityIndicator noTestID color={Theme.loading} size={'large'} />
              </View>
            )}
          </TWCTile>
          <TWCTile
            accessibilityHint={t('accessibilityHintTile')}
            accessibilityLabel={t('accessibilityLabelTileYearly', {
              price: yearly.price,
              savingsPercentage: yearly.savingsPercentage,
            })}
            backgroundDefault={styles.tileDefault}
            height={TILE_SIZES.height}
            nextFocusDown={noFocusYearly}
            nextFocusLeft={monthlyTileHandle}
            nextFocusRight={noFocusYearly}
            nextFocusUp={noFocusYearly}
            styleFocused={styles.tileFocused}
            testID={onboardingTestIDs.subscriptionSelection.yearlyTile}
            useFocusBorder={false}
            useLinearGradient={false}
            width={TILE_SIZES.width}
            onPress={() => {
              if (yearly.sku) {
                requestSubscription(yearly.sku).then(
                  () => {},
                  error => {
                    handleSubscriptionPurchaseError(error, yearly.sku);
                  },
                );
              }
            }}
          >
            {yearly.sku ? (
              <View style={Theme.styles.center}>
                <TWCText noTestID style={styles.yearlyPriceText} variant={'boldText48'}>
                  {`$${yearly.price} / yr`}
                </TWCText>
                <TWCText noTestID style={styles.yearlySavingsText} variant={'regularText36'}>
                  {yearly.body ?? ''}
                </TWCText>
              </View>
            ) : (
              <View style={Theme.styles.center}>
                <ActivityIndicator noTestID color={Theme.loading} size={'large'} />
              </View>
            )}
          </TWCTile>
        </View>

        <TWCText
          style={styles.trialTermsText}
          testID={onboardingTestIDs.subscriptionSelection.trialTermsText}
          variant={'regularText24'}
        >
          {planData.Disclaimer?.headline ?? placeholderText.trialTermsText}
        </TWCText>
        <TWCText testID={onboardingTestIDs.subscriptionSelection.planDisclaimerText} variant={'regularText24'}>
          {planData.Disclaimer?.body ?? placeholderText.planDisclaimerText}
        </TWCText>
      </View>
    </OnboardingBackgroundImage>
  );
};

export const SubscriptionSelectionContent = {
  API: SubscriptionSelectionContentAPI,
  UI: SubscriptionSelectionPageContentUI,
};

const styles = StyleSheet.create({
  headingText: {
    color: Theme.azureWhite,
    marginBottom: Theme.px(5),
  },
  loadingIndicatorView: {
    alignItems: 'center',
    elevation: 200,
    height: Theme.appHeight,
    justifyContent: 'center',
    position: 'absolute',
    width: Theme.appWidth,
    zIndex: 200,
  },
  logo: {
    height: Theme.px(150),
    marginBottom: Theme.px(23),
    marginTop: Theme.px(205),
    width: Theme.px(150),
  },
  pageContent: {
    marginLeft: Theme.px(120),
  },
  subHeadingText: {
    color: Theme.azureWhite,
    lineHeight: Theme.px(48),
    marginBottom: Theme.px(89),
    opacity: 0.7,
    width: Theme.px(720),
  },
  tileDefault: {
    backgroundColor: Theme.withOpacity(Theme.white, 0.1),
    borderRadius: Theme.tileBorderRadius,
  },
  tileFocused: {
    backgroundColor: Theme.alertRed,
    borderRadius: Theme.tileBorderRadius,
  },
  tileRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: Theme.px(51),
    width: Theme.px(948),
  },
  trialTermsText: {
    color: Theme.whisperWhite,
    marginBottom: Theme.px(8),
  },
  yearlyPriceText: {
    marginBottom: Theme.px(5),
    marginTop: Theme.px(42),
  },
  yearlySavingsText: {
    opacity: 0.5,
  },
});
